import { Component } from '@theme/component';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Lazy-loads recently viewed products via the Section Rendering API.
 *
 * Reads product IDs from RecentlyViewed, builds a storefront search URL
 * (q=id:X OR id:Y), fetches the section HTML, and swaps the content in.
 * Mirrors the product-recommendations component pattern.
 */
class RecentlyViewedSection extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.editorPreview === 'true' || window.Shopify?.designMode) {
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  async #load() {
    const { searchUrl, sectionInstanceId, maxProducts, currentProductId } = this.dataset;
    const id = this.id;
    const max = parseInt(maxProducts || '4', 10) || 4;
    const currentId = String(currentProductId ?? '').trim();

    if (!searchUrl || !id) {
      this.#handleError(new Error('Search URL and an ID attribute are required'));
      return;
    }

    const ids = RecentlyViewed.getProducts()
      .filter((productId) => productId && productId !== currentId)
      .slice(0, max);

    if (ids.length === 0) {
      this.#handleError(new Error('No recently viewed products'));
      return;
    }

    try {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('q', ids.map((productId) => `id:${productId}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      const sectionId = normalizeSectionId(sectionInstanceId || id);
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);

      const doc = new DOMParser().parseFromString(html, 'text/html');
      // Fragment response is only [data-recently-viewed-swap] (no custom element wrapper).
      const ajaxSwap = doc.querySelector('[data-recently-viewed-swap]');
      const hostSwap = this.querySelector('[data-recently-viewed-swap]');

      if (!ajaxSwap?.innerHTML.trim() || !hostSwap) {
        this.#handleError(new Error('No recently viewed products available'));
        return;
      }

      hostSwap.innerHTML = ajaxSwap.innerHTML;

      const grid = hostSwap.querySelector('.resource-list');
      if (!grid || grid.getAttribute('data-has-recommendations') !== 'true') {
        this.#handleError(new Error('No recently viewed products available'));
        return;
      }

      this.dataset.recentlyViewedPerformed = 'true';
    } catch (error) {
      this.#handleError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Recently viewed error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading recently viewed products';
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
